import { SelfServeNodes } from "@faire/help-center-self-serve/types";
import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Earn } from "@faire/web--source/slate/icons/foundation/Earn";
import React from "react";

import { AddPaymentMethodIA } from "../instantAnswers/invoicesAndPayments-addPaymentMethod";
import { ChangeDefaultPaymentMethodIA } from "../instantAnswers/invoicesAndPayments-changeDefaultPaymentMethod";
import { Net60BasicsIA } from "../instantAnswers/invoicesAndPayments-net60Basics";
import { PaymentProcessingErrorIA } from "../instantAnswers/invoicesAndPayments-paymentProcessingError";
import { RefundsIA } from "../instantAnswers/invoicesAndPayments-refunds";
import { RequestCreditLimitIncreaseIA } from "../instantAnswers/invoicesAndPayments-requestCreditLimitIncrease";
import { ViewOrUpdatePaymentMethodsIA } from "../instantAnswers/invoicesAndPayments-viewOrUpdatePaymentMethods";

export const usePaymentsRetailerNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    Payments: {
      id: "Payments",
      label: strictLocalize({
        defaultMessage: "Payments",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Earn fontSize="24px" color="faireBlack" />,
      children: ["PaymentMethods", "PaymentIssues", "PaymentTerms", "Refunds"],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Payments issue",
        description: {
          text: "Title for displaying on the order summary sidebar",
        },
      }),
    },
    PaymentTerms: {
      id: "PaymentTerms",
      label: strictLocalize({
        defaultMessage: "Payment terms",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["AboutPaymentTerms", "RequestCreditLimitIncrease"],
      level: 2,
    },
    PaymentMethods: {
      id: "PaymentMethods",
      label: strictLocalize({
        defaultMessage: "Payment methods",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "AddPaymentMethod",
        "ChangeDefaultPaymentMethod",
        "EditPaymentMethods",
      ],
      level: 2,
    },
    PaymentIssues: {
      id: "PaymentIssues",
      label: strictLocalize({
        defaultMessage: "Payment issues",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["PaymentProcessingError"],
      level: 2,
    },
    Refunds: {
      id: "Refunds",
      label: strictLocalize({
        defaultMessage: "Refunds",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: RefundsIA(strictLocalize),
      articles: [articles.ReturnsAndRefunds.MAKE_A_RETURN],
      level: 2,
    },
    AboutPaymentTerms: {
      id: "AboutPaymentTerms",
      label: strictLocalize({
        defaultMessage: "About payment terms",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: Net60BasicsIA(strictLocalize),
      articles: [articles.PaymentsAndInvoices.NET_60_TERMS],
      level: 3,
    },
    RequestCreditLimitIncrease: {
      id: "RequestCreditLimitIncrease",
      label: strictLocalize({
        defaultMessage: "Request credit limit increase",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: RequestCreditLimitIncreaseIA(strictLocalize),
      articles: [articles.PaymentsAndInvoices.NET_60_TERMS?.atSection("2")],
      level: 3,
    },
    AddPaymentMethod: {
      id: "AddPaymentMethod",
      label: strictLocalize({
        defaultMessage: "Add payment method",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: AddPaymentMethodIA(strictLocalize),
      articles: [
        articles.PaymentsAndInvoices.HOW_DO_I_PAY_FOR_ORDERS,
        articles.PaymentsAndInvoices.CHANGE_MY_PAYMENT_METHOD,
      ],
      level: 3,
    },
    ChangeDefaultPaymentMethod: {
      id: "ChangeDefaultPaymentMethod",
      label: strictLocalize({
        defaultMessage: "Change default payment method",
      }),
      instantAnswer: ChangeDefaultPaymentMethodIA(strictLocalize),
      articles: [articles.PaymentsAndInvoices.CHANGE_MY_PAYMENT_METHOD],
      level: 3,
    },
    EditPaymentMethods: {
      id: "EditPaymentMethods",
      label: strictLocalize({
        defaultMessage: "Edit payment methods",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ViewOrUpdatePaymentMethodsIA(strictLocalize),
      articles: [
        articles.PaymentsAndInvoices.HOW_DO_I_PAY_FOR_ORDERS,
        articles.PaymentsAndInvoices.CHANGE_MY_PAYMENT_METHOD,
      ],
      level: 3,
    },
    PaymentProcessingError: {
      id: "PaymentProcessingError",
      label: strictLocalize({
        defaultMessage: "Payment processing error",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: PaymentProcessingErrorIA(strictLocalize),
      level: 3,
    },
  };
};
